import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Trash, Copy, Search } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { brl, BADGES } from "@/lib/format";
import { Card, Field, TextArea, SaveBtn, Toggle, Empty } from "./ui";
import { SmartImage } from "../public/media";
import Uploader from "../common/Uploader";

const BADGE_KEYS = ["mais_vendido", "top", "promo", "novo", "classico"];
const uid = () => Math.random().toString(36).slice(2, 10);

export default function ProductsModule({ rid, data, reload }) {
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const cats = data.categories;
  const noCats = cats.length === 0;
  const catName = (id) => cats.find((c) => c.id === id)?.name || "Sem categoria";

  const newProduct = () => {
    if (noCats) return toast.error("Crie uma categoria antes de cadastrar produtos.");
    setEditing({ new: true });
  };

  const del = async (p) => {
    if (!window.confirm(`Excluir "${p.name}"?`)) return;
    try { await api.delete(`/restaurants/${rid}/products/${p.id}`); toast.success("Produto excluído"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const duplicate = async (p) => {
    try {
      const { id, created_at, ...rest } = p;
      await api.post(`/restaurants/${rid}/products`, { ...rest, name: `${p.name} (cópia)`, is_best_seller: false, best_seller_order: 0 });
      toast.success("Produto duplicado"); reload();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const filtered = useMemo(() => {
    return data.products.filter((p) => {
      if (catFilter !== "all" && p.category_id !== catFilter) return false;
      if (statusFilter === "active" && (p.available === false || p.sold_out)) return false;
      if (statusFilter === "paused" && p.available !== false) return false;
      if (statusFilter === "sold_out" && !p.sold_out) return false;
      if (q && !`${p.name} ${p.description}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [data.products, q, catFilter, statusFilter]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((p) => { (g[p.category_id] = g[p.category_id] || []).push(p); });
    return g;
  }, [filtered]);

  return (
    <div className="space-y-4" data-testid="products-module">
      {noCats && (
        <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-4 text-sm text-amber-300" data-testid="no-categories-warning">
          Crie uma categoria antes de cadastrar produtos.
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="flex-1 flex items-center gap-2 rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2">
          <Search size={16} className="text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="product-search" placeholder="Buscar produto..." className="flex-1 bg-transparent outline-none text-sm" />
        </div>
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="product-filter-cat" className="rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2.5 text-sm outline-none">
          <option value="all" className="bg-zinc-900">Todas categorias</option>
          {cats.map((c) => <option key={c.id} value={c.id} className="bg-zinc-900">{c.name}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="product-filter-status" className="rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2.5 text-sm outline-none">
          <option value="all" className="bg-zinc-900">Todos status</option>
          <option value="active" className="bg-zinc-900">Ativos</option>
          <option value="paused" className="bg-zinc-900">Pausados</option>
          <option value="sold_out" className="bg-zinc-900">Esgotados</option>
        </select>
        <button onClick={newProduct} data-testid="product-add" className="flex items-center justify-center gap-2 rounded-full hmmm-gradient px-4 py-2.5 font-bold hmmm-glow text-white"><Plus size={18} /> Novo</button>
      </div>

      {data.products.length === 0 && !noCats && <Empty>Nenhum produto. Adicione o primeiro.</Empty>}
      {filtered.length === 0 && data.products.length > 0 && <Empty>Nenhum produto encontrado.</Empty>}

      {cats.map((c) => (grouped[c.id]?.length ? (
        <div key={c.id}>
          <p className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2 px-1">{c.name} · {grouped[c.id].length}</p>
          <div className="space-y-2">
            {grouped[c.id].map((p) => <Row key={p.id} p={p} onEdit={() => setEditing(p)} onDel={() => del(p)} onDup={() => duplicate(p)} catName={catName} />)}
          </div>
        </div>
      ) : null))}
      {grouped["undefined"]?.length ? (
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-rose-400 mb-2 px-1">Sem categoria</p>
          <div className="space-y-2">{grouped["undefined"].map((p) => <Row key={p.id} p={p} onEdit={() => setEditing(p)} onDel={() => del(p)} onDup={() => duplicate(p)} catName={catName} />)}</div>
        </div>
      ) : null}

      {editing && <ProductEditor rid={rid} product={editing.new ? null : editing} cats={cats} products={data.products} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

function Row({ p, onEdit, onDel, onDup }) {
  return (
    <Card className="!p-3">
      <div className="flex items-center gap-3">
        <SmartImage src={p.image} className="h-14 w-14 rounded-xl shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold truncate">{p.name}</p>
            {p.sold_out && <span className="text-[10px] bg-zinc-700 px-1.5 py-0.5 rounded">Esgotado</span>}
            {p.available === false && <span className="text-[10px] bg-amber-600/30 text-amber-300 px-1.5 py-0.5 rounded">Pausado</span>}
            {(p.badges || []).slice(0, 2).map((b) => BADGES[b] && <span key={b} className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${BADGES[b].cls}`}>{BADGES[b].label}</span>)}
          </div>
          <p className="text-sm text-zinc-500">{p.promo_price ? <><span className="line-through mr-1">{brl(p.price)}</span><span className="hmmm-text font-bold">{brl(p.promo_price)}</span></> : brl(p.price)}</p>
        </div>
        <button onClick={onDup} data-testid={`product-dup-${p.id}`} title="Duplicar" className="h-9 w-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center"><Copy size={15} /></button>
        <button onClick={onEdit} data-testid={`product-edit-${p.id}`} className="h-9 w-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center"><Pencil size={15} /></button>
        <button onClick={onDel} data-testid={`product-del-${p.id}`} className="h-9 w-9 rounded-lg bg-white/5 hover:bg-rose-600 flex items-center justify-center"><Trash2 size={15} /></button>
      </div>
    </Card>
  );
}

function ProductEditor({ rid, product, cats, products, onClose, onSaved }) {
  const [f, setF] = useState(product || {
    name: "", description: "", category_id: "", price: "", promo_price: "",
    prep_time: 15, image: null, available: true, sold_out: false, badges: [], is_best_seller: false,
    best_seller_order: 0, on_offer: false, addon_groups: [], upsell_product_ids: [], upsell_location: "both",
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleBadge = (b) => set("badges", f.badges.includes(b) ? f.badges.filter((x) => x !== b) : [...f.badges, b]);
  const toggleUpsell = (id) => set("upsell_product_ids", f.upsell_product_ids.includes(id) ? f.upsell_product_ids.filter((x) => x !== id) : [...f.upsell_product_ids, id]);

  const addGroup = () => set("addon_groups", [...f.addon_groups, { id: uid(), name: "Novo grupo", required: false, min: 0, max: 1, items: [] }]);
  const updGroup = (gid, patch) => set("addon_groups", f.addon_groups.map((g) => g.id === gid ? { ...g, ...patch } : g));
  const delGroup = (gid) => set("addon_groups", f.addon_groups.filter((g) => g.id !== gid));
  const addItem = (gid) => updGroup(gid, { items: [...f.addon_groups.find((g) => g.id === gid).items, { id: uid(), name: "", price: 0 }] });
  const updItem = (gid, iid, patch) => { const g = f.addon_groups.find((x) => x.id === gid); updGroup(gid, { items: g.items.map((it) => it.id === iid ? { ...it, ...patch } : it) }); };
  const delItem = (gid, iid) => { const g = f.addon_groups.find((x) => x.id === gid); updGroup(gid, { items: g.items.filter((it) => it.id !== iid) }); };

  const save = async () => {
    if (!f.name.trim()) return toast.error("Informe o nome");
    if (!f.category_id) return toast.error("Selecione uma categoria (obrigatória)");
    setLoading(true);
    const payload = {
      ...f, price: Number(f.price) || 0, promo_price: f.promo_price ? Number(f.promo_price) : null,
      prep_time: Number(f.prep_time) || 0, best_seller_order: Number(f.best_seller_order) || 0,
      addon_groups: f.addon_groups.map((g) => ({ ...g, min: Number(g.min) || 0, max: Number(g.max) || 0, items: g.items.map((it) => ({ ...it, price: Number(it.price) || 0 })) })),
    };
    try {
      if (product) await api.put(`/restaurants/${rid}/products/${product.id}`, payload);
      else await api.post(`/restaurants/${rid}/products`, payload);
      toast.success(product ? "Produto atualizado" : "Produto criado com sucesso");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center px-3 py-6 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#141417] border border-white/10 rounded-3xl p-6 my-auto" onClick={(e) => e.stopPropagation()} data-testid="product-editor">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head text-lg font-extrabold">{product ? "Editar produto" : "Novo produto"}</h3>
          <button onClick={onClose}><X size={20} className="text-zinc-500" /></button>
        </div>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto no-scrollbar pr-1">
          <Uploader value={f.image} onChange={(v) => set("image", v)} label="Foto do produto" tid="pe-image" />
          <Field label="Nome" value={f.name} onChange={(v) => set("name", v)} tid="pe-name" />
          <TextArea label="Descrição" value={f.description} onChange={(v) => set("description", v)} tid="pe-desc" />
          <div>
            <label className="text-xs font-semibold text-zinc-400">Categoria <span className="text-rose-400">*</span></label>
            <select value={f.category_id || ""} onChange={(e) => set("category_id", e.target.value)} data-testid="pe-category" className="w-full mt-1 rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2.5 outline-none text-sm">
              <option value="" className="bg-zinc-900">Selecione uma categoria</option>
              {cats.map((c) => <option key={c.id} value={c.id} className="bg-zinc-900">{c.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Preço (R$)" type="number" value={f.price} onChange={(v) => set("price", v)} tid="pe-price" />
            <Field label="Promo (R$)" type="number" value={f.promo_price} onChange={(v) => set("promo_price", v)} tid="pe-promo" />
            <Field label="Preparo (min)" type="number" value={f.prep_time} onChange={(v) => set("prep_time", v)} tid="pe-prep" />
          </div>
          <div>
            <label className="text-xs font-semibold text-zinc-400">Badges</label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {BADGE_KEYS.map((b) => (
                <button key={b} onClick={() => toggleBadge(b)} data-testid={`pe-badge-${b}`} className={`text-xs font-bold px-3 py-1.5 rounded-full ${f.badges.includes(b) ? BADGES[b].cls : "bg-white/5 text-zinc-400"}`}>{BADGES[b].label}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center justify-between rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2.5"><span className="text-sm">Disponível</span><Toggle checked={f.available} onChange={(v) => set("available", v)} tid="pe-available" /></div>
            <div className="flex items-center justify-between rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2.5"><span className="text-sm">Esgotado</span><Toggle checked={f.sold_out} onChange={(v) => set("sold_out", v)} tid="pe-soldout" /></div>
            <div className="flex items-center justify-between rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2.5"><span className="text-sm">Mais vendido</span><Toggle checked={f.is_best_seller} onChange={(v) => set("is_best_seller", v)} tid="pe-best" /></div>
            <div className="flex items-center justify-between rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2.5"><span className="text-sm">Em oferta</span><Toggle checked={f.on_offer} onChange={(v) => set("on_offer", v)} tid="pe-offer" /></div>
          </div>
          {f.on_offer && !f.promo_price && <p className="text-xs text-amber-400">Defina um preço promocional para aparecer nas Ofertas.</p>}
          <div className="pt-2 border-t border-white/10">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-zinc-400">Adicionais</label>
              <button onClick={addGroup} data-testid="pe-add-group" className="text-xs font-semibold hmmm-text">+ Grupo</button>
            </div>
            <div className="space-y-3">
              {f.addon_groups.map((g) => (
                <div key={g.id} className="rounded-xl border border-white/10 p-3 bg-white/[0.02]">
                  <div className="flex gap-2 items-center">
                    <input value={g.name} onChange={(e) => updGroup(g.id, { name: e.target.value })} className="flex-1 rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm outline-none" placeholder="Nome do grupo" />
                    <button onClick={() => delGroup(g.id)} className="text-zinc-500 hover:text-rose-500"><Trash size={15} /></button>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-zinc-400">
                    <label className="flex items-center gap-1.5"><input type="checkbox" checked={g.required} onChange={(e) => updGroup(g.id, { required: e.target.checked })} /> Obrigatório</label>
                    <label className="flex items-center gap-1">Mín <input type="number" value={g.min} onChange={(e) => updGroup(g.id, { min: e.target.value })} className="w-12 rounded bg-white/[0.04] border border-white/10 px-1.5 py-1 outline-none" /></label>
                    <label className="flex items-center gap-1">Máx <input type="number" value={g.max} onChange={(e) => updGroup(g.id, { max: e.target.value })} className="w-12 rounded bg-white/[0.04] border border-white/10 px-1.5 py-1 outline-none" /></label>
                  </div>
                  <div className="space-y-1.5 mt-2">
                    {g.items.map((it) => (
                      <div key={it.id} className="flex gap-2 items-center">
                        <input value={it.name} onChange={(e) => updItem(g.id, it.id, { name: e.target.value })} className="flex-1 rounded-lg bg-white/[0.04] border border-white/10 px-2.5 py-1.5 text-sm outline-none" placeholder="Item" />
                        <input type="number" value={it.price} onChange={(e) => updItem(g.id, it.id, { price: e.target.value })} className="w-20 rounded-lg bg-white/[0.04] border border-white/10 px-2.5 py-1.5 text-sm outline-none" placeholder="R$" />
                        <button onClick={() => delItem(g.id, it.id)} className="text-zinc-500 hover:text-rose-500"><X size={15} /></button>
                      </div>
                    ))}
                    <button onClick={() => addItem(g.id)} className="text-xs hmmm-text font-semibold">+ Item</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="pt-2 border-t border-white/10">
            <label className="text-xs font-semibold text-zinc-400">Venda guiada / Upsell — "Complete seu pedido"</label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {[["produto", "Tela do produto"], ["carrinho", "Carrinho"], ["both", "Ambos"]].map(([v, l]) => (
                <button key={v} onClick={() => set("upsell_location", v)} data-testid={`pe-upsell-loc-${v}`} className={`text-xs font-semibold py-2 rounded-lg ${f.upsell_location === v ? "hmmm-gradient text-white" : "bg-white/5 text-zinc-400"}`}>{l}</button>
              ))}
            </div>
            <div className="mt-2 max-h-40 overflow-y-auto no-scrollbar space-y-1">
              {products.filter((p) => p.id !== (product?.id)).map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm py-1 px-2 rounded-lg hover:bg-white/5">
                  <input type="checkbox" checked={f.upsell_product_ids.includes(p.id)} onChange={() => toggleUpsell(p.id)} data-testid={`pe-upsell-${p.id}`} />
                  <span className="truncate">{p.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-white/10">
          <button onClick={onClose} className="rounded-full bg-white/10 px-5 py-2.5 font-semibold">Cancelar</button>
          <SaveBtn onClick={save} loading={loading} tid="pe-save" />
        </div>
      </div>
    </div>
  );
}
